import { validateFilters } from '@cian/valuation-main-filters-widget';
import { BadRequestError } from '@cian/valuation-utils-component';

import { checkEstimationExistsRequest, checkEstimationExistsRequestError, resetCheckEstimationExists } from './actions';
import { mapEstimationTargetToECategory } from '../../constants/filters';
import {
  fetchGetEstimationSuburban,
  IGetEstimationSuburbanResponse200 as ISuburbanRes200,
  IGetEstimationSuburbanResponse400 as ISuburbanRes400,
} from '../../repositories/price-estimator/v1/get-estimation-suburban';
import {
  fetchCheckEstimationExists,
  ICheckEstimationExistsResponse200,
  ICheckEstimationExistsResponse400,
} from '../../repositories/price-estimator/v4/check-estimation-exists';
import { TErrorApi } from '../../types/error';
import { TThunkAction } from '../../types/redux';

export const checkEstimationExists = (): TThunkAction<Promise<boolean>> => {
  return async (dispatch, getState, context) => {
    dispatch(checkEstimationExistsRequest());

    const isFormValid = await dispatch(validateFilters());

    if (!isFormValid) {
      dispatch(resetCheckEstimationExists());

      return false;
    }

    /**
     * getState вызываем после валидации формы чтобы убедится что все поля заполнены
     */
    const {
      filters: { roomsCount, area, address },
    } = getState();
    const { httpApi, logger } = context;

    try {
      const res = await fetchCheckEstimationExists<
        ICheckEstimationExistsResponse200,
        ICheckEstimationExistsResponse400
      >({
        httpApi,
        parameters: {
          address,
          roomsCount: roomsCount as number,
          area: area as number,
        },
      });

      if (res.statusCode !== 200) {
        throw new BadRequestError({
          domain: 'checkEstimationExists.ts',
          details: { errors: res.response.errors as TErrorApi },
          message: res.response.message as string,
        });
      }

      const anyEstimationExists = isAnyEstimationExists(res.response);

      if (!anyEstimationExists) {
        dispatch(checkEstimationExistsRequestError());

        return false;
      }

      return true;
    } catch (e) {
      if (e instanceof BadRequestError) {
        logger.debug('checkEstimationExists', { details: e.printDetails() });
      } else {
        logger.error(e, { message: 'Unable to check estimation exists' });
      }

      dispatch(checkEstimationExistsRequestError());

      return false;
    }
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isAnyEstimationExists(estimations: { [key: string]: any }): boolean {
  return Object.values(estimations).some(Boolean);
}

export const checkSuburbanEstimationExists = (): TThunkAction<Promise<boolean>> => {
  return async (dispatch, getState, context) => {
    dispatch(checkEstimationExistsRequest());

    const isFormValid = await dispatch(validateFilters());

    if (!isFormValid) {
      dispatch(resetCheckEstimationExists());

      return false;
    }

    /**
     * getState вызываем после валидации формы чтобы убедится что все поля заполнены
     */
    const {
      filters: { address, houseArea, landArea, estimationTarget },
    } = getState();
    const { httpApi, logger } = context;

    try {
      const res = await fetchGetEstimationSuburban<ISuburbanRes200, ISuburbanRes400>({
        httpApi,
        parameters: {
          address,
          houseArea,
          landArea: landArea as number,
          category: mapEstimationTargetToECategory[estimationTarget],
        },
      });

      if (res.statusCode !== 200) {
        throw new BadRequestError({
          domain: 'checkSuburbanEstimationExists.ts',
          details: { errors: res.response.errors as TErrorApi },
          message: res.response.message as string,
        });
      }

      if (!res.response.sale) {
        dispatch(checkEstimationExistsRequestError());

        return false;
      }

      return true;
    } catch (e) {
      if (e instanceof BadRequestError) {
        logger.debug('checkSuburbanEstimationExists', { details: e.printDetails() });
      } else {
        logger.error(e, { message: 'Unable to check suburban estimation exists' });
      }

      dispatch(checkEstimationExistsRequestError());

      return false;
    }
  };
};
